import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, User, Mail, Phone, MapPin, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  'Mutual Funds',
  'Fixed Deposits',
  'Insurance',
  'Bonds',
  'AIF Consultation',
  'Real Estate',
  'Portfolio Review',
  'General Consultation',
];

const Appointment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    mobile: '',
    email: '',
    city: '',
    client_type: 'new',
    consultation_type: 'in_person',
    service_required: SERVICE_OPTIONS.includes(preselectedService) ? preselectedService : 'Mutual Funds',
    preferred_date: '',
    preferred_time: '',
    investment_range: '',
    message: ''
  });
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (preselectedService && SERVICE_OPTIONS.includes(preselectedService)) {
      setFormData((prev) => ({ ...prev, service_required: preselectedService }));
    }
  }, [preselectedService]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!consent) {
      toast.error('Please accept the consent to proceed.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/appointments`, formData);
      toast.success('Appointment request submitted successfully! We will contact you shortly.');
      navigate('/');
    } catch (error) {
      console.error('Error submitting appointment:', error);
      toast.error('Failed to submit appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="appointment-page">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="appointment-heading">
            Book an Appointment
          </h1>
          <p className="text-base font-body text-[#4A4A4A] max-w-3xl mx-auto leading-relaxed">
            Schedule a consultation with our team to discuss your financial goals and investment planning needs.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FDF8F5] rounded-2xl p-8 lg:p-12 border border-[#E5E5E5]">
            <form onSubmit={handleSubmit} data-testid="appointment-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-mobile-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-city-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    Client Type *
                  </label>
                  <select
                    name="client_type"
                    value={formData.client_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-client-type-select"
                  >
                    <option value="new">New Client</option>
                    <option value="existing">Existing Client</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    Consultation Type *
                  </label>
                  <select
                    name="consultation_type"
                    value={formData.consultation_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-consultation-type-select"
                  >
                    <option value="in_person">In-Person Consultation</option>
                    <option value="phone">Phone Consultation</option>
                    <option value="video">Video Consultation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    Service Required *
                  </label>
                  <select
                    name="service_required"
                    value={formData.service_required}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-service-select"
                  >
                    <option value="Mutual Funds">Mutual Funds</option>
                    <option value="Fixed Deposits">Fixed Deposits</option>
                    <option value="Insurance">Insurance</option>
                    <option value="Bonds">Bonds</option>
                    <option value="AIF Consultation">Alternative Investment Funds (AIFs)</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Portfolio Review">Portfolio Review</option>
                    <option value="General Consultation">General Consultation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    name="preferred_date"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-date-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    <Clock className="w-4 h-4 inline mr-2" />
                    Preferred Time *
                  </label>
                  <select
                    name="preferred_time"
                    value={formData.preferred_time}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-time-select"
                  >
                    <option value="">Select Time</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                    Approximate Investment Range
                  </label>
                  <select
                    name="investment_range"
                    value={formData.investment_range}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                    data-testid="appointment-investment-range-select"
                  >
                    <option value="">Select Range</option>
                    <option value="Below 1 Lakh">Below ₹1 Lakh</option>
                    <option value="1-5 Lakhs">₹1 - 5 Lakhs</option>
                    <option value="5-10 Lakhs">₹5 - 10 Lakhs</option>
                    <option value="10-25 Lakhs">₹10 - 25 Lakhs</option>
                    <option value="25-50 Lakhs">₹25 - 50 Lakhs</option>
                    <option value="Above 50 Lakhs">Above ₹50 Lakhs</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-body font-medium text-[#4A4A4A] mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Message or Financial Objective
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 focus:border-transparent font-body bg-white"
                  data-testid="appointment-message-input"
                />
              </div>

              <div className="mb-6">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 mr-3"
                    data-testid="appointment-consent-checkbox"
                  />
                  <span className="text-sm font-body text-[#4A4A4A]">
                    I agree to be contacted by Impetus Investment Services regarding my enquiry and understand that submitting this form does not constitute investment advice or a commitment to invest. *
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
                data-testid="appointment-submit-btn"
              >
                {loading ? 'Submitting...' : 'Submit Appointment Request'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Appointment;